import api from '../../lib/axios';
import type { User } from '../../types';
import type { SubscriptionPlanType } from '../../types/payments';

export interface SessionToken {
    access_token: string;
    token_type: string;
    expires_in: number;
    user: User;
}

export interface PaidSignupPayload {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    entry_code: string;
    plan?: string;
}

export interface BusinessProfilePayload {
    business_sector: string;
    use_cases: string[];
    custom_use_case?: string;
}

export type PlanPrices = Partial<Record<SubscriptionPlanType, number>>;

export const paidSignupApi = {
    /** Prices the backend will charge (public; the page shows these, not the static plan prices). */
    getPlanPrices: async (): Promise<PlanPrices> => {
        const res = await api.get<{ plans: { plan_type: SubscriptionPlanType; price: number }[] }>(
            '/auth/paid-signup/plans'
        );
        return Object.fromEntries(res.data.plans.map(p => [p.plan_type, p.price]));
    },

    getRegion: async (country?: string): Promise<{
        is_international: boolean;
        currency: string;
        country_code: string;
        plans: PlanPrices;
    }> => {
        const headers = country ? { 'X-Country-Code': country } : {};
        const res = await api.get('/auth/paid-signup/region', { headers });
        return res.data;
    },

    register: async (payload: PaidSignupPayload): Promise<SessionToken> => {
        const res = await api.post('/auth/register-paid', payload);
        return res.data;
    },

    updateBusinessProfile: async (payload: BusinessProfilePayload): Promise<User> => {
        const res = await api.put('/auth/me/business-profile', payload);
        return res.data;
    },

    requestEmailChange: async (newEmail: string): Promise<void> => {
        await api.post('/auth/me/email/change-request', { new_email: newEmail });
    },

    confirmEmailChange: async (newEmail: string, otpCode: string): Promise<SessionToken> => {
        const res = await api.post('/auth/me/email/change-confirm', { new_email: newEmail, otp_code: otpCode });
        return res.data;
    },
};

export type PaidSignupStep = 'payment' | 'business' | 'verify';

/**
 * Pending step of the campaign-URL signup, derived from the user record.
 * `null` means the account can use the app.
 */
export function getPaidSignupStep(user: User | null | undefined): PaidSignupStep | null {
    if (!user) return null;
    if (user.payment_required) return 'payment';
    if (user.signup_flow !== 'paywall') return null;
    if (!user.business_sector) return 'business';
    if (user.is_verified === false) return 'verify';
    return null;
}
